// ============================================================================
// FileCollection.tsx — ЕДИНЫЙ виджет коллекции файлов (SRP/SSoT):
// варианты отображения (ГЛОБАЛЬНЫЕ виджеты, применяются в ТЧ «Поле»,
// «Файлах по документам», на кастомных страницах):
//   card    — Карточка: thumb + выводимые поля (settings.cardFields)
//   gallery — Галерея: крупные превью изображений (settings.size)
//   tile    — Тайл: компактная плитка (иконка/thumb + имя)
//   list    — Список: строки (иконка + имя + размер)
//   table   — Таблица: колонки (settings.columns)
// Данные — FileDetail[] (SSoT — сервер, ListFiles/GetFileInfo).
// Thumb-превью изображений — кэш по id (downloadFile).
// ============================================================================

import { useEffect, useState } from 'react';
import { FileText, Download, Eye, X } from 'lucide-react';
import { getSDK } from '@/store/sdkStore';
import { fileBytes } from '@/lib/fileBytes';

export interface FileDetail {
  id: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  uploadedBy?: string;
  uploadedAt?: string;
}

export type FileCollectionView = 'card' | 'gallery' | 'tile' | 'list' | 'table';

export interface FileCollectionSettings {
  /** Карточка: выводимые поля (name/size/uploaded_by/uploaded_at) */
  cardFields?: string[];
  /** Галерея: размер превью (small/medium/large) */
  size?: 'small' | 'medium' | 'large';
  /** Тайл: показывать имя */
  showName?: boolean;
  /** Список: показывать размер */
  showSize?: boolean;
  /** Таблица: колонки (name/size/uploaded_by/uploaded_at) */
  columns?: string[];
}

// Кэш thumb-превью изображений по id файла (data URL) — SSoT: downloadFile
const thumbCache = new Map<string, string>();

export const FILE_VIEW_LABELS: Record<FileCollectionView, string> = {
  card: 'Карточка',
  gallery: 'Галерея',
  tile: 'Тайл',
  list: 'Список',
  table: 'Таблица',
};

const isImage = (f: FileDetail) => (f.fileType || '').startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(f.fileName || '');

function fmtSize(n: number): string {
  return n >= 1048576 ? `${(n / 1048576).toFixed(1)} МБ` : n >= 1024 ? `${(n / 1024).toFixed(1)} КБ` : `${n} Б`;
}
function fmtDate(s: string): string {
  return s ? new Date(s).toLocaleString('ru-RU') : '';
}

/** Превью-иконка/thumb для файла (кэш; не изображения — иконка документа).
 *  Серверная миниатюра (GetFileThumb — JPEG, 320px) вместо ПОЛНОГО файла:
 *  скорость/трафик (картинки большие; видео — кадр). SSoT — сервер. */
function FileThumb({ file, className }: { file: FileDetail; className?: string }) {
  const [url, setUrl] = useState(thumbCache.get(file.id) || '');
  useEffect(() => {
    if (thumbCache.has(file.id)) return;
    let cancelled = false;
    getSDK().files.getFileThumb({ id: file.id, width: 320 } as any)
      .then((resp: any) => {
        if (cancelled) return;
        const bytes = fileBytes((resp as any)?.data);
        if (!bytes || bytes.length === 0) return;
        const blob = new Blob([bytes as unknown as BlobPart], { type: 'image/jpeg' });
        const u = URL.createObjectURL(blob);
        thumbCache.set(file.id, u);
        setUrl(u);
      })
      .catch(() => { /* превью недоступно — иконка документа */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file.id]);
  if (url) {
    return <img src={url} alt={file.fileName} className={className || 'h-full w-full object-cover'} />;
  }
  return <FileText className={className || 'h-6 w-6 text-muted-foreground'} />;
}

interface FileCollectionProps {
  files: FileDetail[];
  view?: FileCollectionView;
  settings?: FileCollectionSettings;
  /** Клик по файлу (действие по умолчанию) */
  onPick?: (file: FileDetail) => void;
  onRemove?: (file: FileDetail) => void;
  /** Показывать кнопки действий (Скачать/Предпросмотр) */
  actions?: boolean;
  /** Пустое состояние */
  emptyText?: string;
  disabled?: boolean;
  /** Выбранные id (подсветка строк/карточек; чекбоксы в таблице) */
  selectedIds?: Set<string>;
}

export function FileCollection({ files, view = 'tile', settings = {}, onPick, onRemove, actions = true, emptyText = 'Нет файлов', disabled, selectedIds }: FileCollectionProps) {
  const download = (f: FileDetail) => {
    void (async () => {
      try {
        const resp = await getSDK().files.downloadFile({ id: f.id });
        const blob = new Blob([resp.data as any], { type: f.fileType || 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = resp.fileName || f.fileName || 'file';
        a.click();
        URL.revokeObjectURL(url);
      } catch { /* ошибка скачивания — молчание недопустимо */ }
    })();
  };

  if (files.length === 0) {
    return <div className="text-[10px] text-muted-foreground">{emptyText}</div>;
  }

  const clickable = (f: FileDetail) => !disabled && onPick ? () => onPick(f) : undefined;
  const rowActions = (f: FileDetail) => actions && !disabled && (
    <span className="flex items-center gap-0.5 ml-auto shrink-0">
      <button title="Скачать" className="p-0.5 rounded hover:bg-muted/60 text-muted-foreground" onClick={(e) => { e.stopPropagation(); download(f); }}><Download className="h-3 w-3" /></button>
      {onRemove && <button title="Удалить" className="p-0.5 rounded hover:bg-muted/60 text-destructive" onClick={(e) => { e.stopPropagation(); onRemove(f); }}><X className="h-3 w-3" /></button>}
    </span>
  );

  // ===== Таблица =====
  if (view === 'table') {
    const cols = settings.columns && settings.columns.length > 0 ? settings.columns : ['name', 'size', 'uploaded_by', 'uploaded_at'];
    const headers: Record<string, string> = { name: 'Имя', size: 'Размер', uploaded_by: 'Загрузил', uploaded_at: 'Дата загрузки' };
    return (
      <div className="border rounded-md overflow-hidden">
        <table className="w-full text-xs">
          <thead><tr className="border-b bg-muted/30 text-left">
            {onPick && <th className="w-8" />}
            {cols.map((c) => <th key={c} className="px-2 py-1 font-medium">{headers[c] || c}</th>)}
            {actions && <th className="w-10" />}
          </tr></thead>
          <tbody>
            {files.map((f) => (
              <tr key={f.id} className={`border-b last:border-b-0 cursor-pointer hover:bg-accent/30 ${selectedIds?.has(f.id) ? 'bg-primary/5' : ''}`} onClick={clickable(f)}>
                {onPick && <td className="px-2 py-1 w-8"><input type="checkbox" checked={!!selectedIds?.has(f.id)} onChange={(e) => { e.stopPropagation(); onPick(f); }} className="rounded" /></td>}
                {cols.includes('name') && <td className="px-2 py-1">{f.fileName}</td>}
                {cols.includes('size') && <td className="px-2 py-1 text-muted-foreground">{fmtSize(f.fileSize)}</td>}
                {cols.includes('uploaded_by') && <td className="px-2 py-1 text-muted-foreground">{f.uploadedBy || '—'}</td>}
                {cols.includes('uploaded_at') && <td className="px-2 py-1 text-muted-foreground">{fmtDate(f.uploadedAt || '')}</td>}
                {actions && <td className="px-1 py-1">{rowActions(f)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // ===== Список =====
  if (view === 'list') {
    return (
      <div className="space-y-0.5">
        {files.map((f) => (
          <div key={f.id} className={`flex items-center gap-2 border rounded px-2 py-1 text-xs hover:bg-accent/30 cursor-pointer ${selectedIds?.has(f.id) ? 'border-primary ring-1 ring-primary bg-primary/5' : ''}`} onClick={clickable(f)}>
            <span className="shrink-0"><FileThumb file={f} className="h-4 w-4 text-muted-foreground" /></span>
            <span className="truncate min-w-0">{f.fileName}</span>
            {settings.showSize !== false && <span className="text-muted-foreground shrink-0">{fmtSize(f.fileSize)}</span>}
            {rowActions(f)}
          </div>
        ))}
      </div>
    );
  }

  // ===== Тайл =====
  if (view === 'tile') {
    return (
      <div className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-1.5">
        {files.map((f) => (
          <div key={f.id} className={`border rounded p-1.5 flex items-center gap-1.5 hover:bg-accent/30 cursor-pointer ${selectedIds?.has(f.id) ? 'border-primary ring-1 ring-primary bg-primary/5' : ''}`} onClick={clickable(f)}>
            <span className="h-8 w-8 shrink-0 rounded border overflow-hidden bg-muted/30 flex items-center justify-center">
              <FileThumb file={f} className="h-full w-full object-cover" />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-[10px] truncate">{settings.showName === false ? '' : f.fileName}</span>
              <span className="block text-[9px] text-muted-foreground">{fmtSize(f.fileSize)}</span>
            </span>
          </div>
        ))}
      </div>
    );
  }

  // ===== Галерея =====
  if (view === 'gallery') {
    const sizeMap = { small: 80, medium: 120, large: 180 };
    const px = sizeMap[settings.size || 'medium'];
    return (
      <div className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-1.5">
        {files.map((f) => (
          <div key={f.id} className={`border rounded p-1 hover:bg-accent/30 cursor-pointer ${selectedIds?.has(f.id) ? 'border-primary ring-1 ring-primary bg-primary/5' : ''}`} onClick={clickable(f)}>
            <div className="rounded overflow-hidden bg-muted/30 flex items-center justify-center" style={{ height: px }}>
              <FileThumb file={f} className="h-full w-full object-cover" />
            </div>
            <div className="text-[10px] truncate mt-0.5">{f.fileName}</div>
          </div>
        ))}
      </div>
    );
  }

  // ===== Карточка =====
  const cardFields = settings.cardFields && settings.cardFields.length > 0 ? settings.cardFields : ['name', 'size'];
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-1.5">
      {files.map((f) => (
        <div key={f.id} className={`border rounded p-2 space-y-1 hover:bg-accent/30 cursor-pointer ${selectedIds?.has(f.id) ? 'border-primary ring-1 ring-primary bg-primary/5' : ''}`} onClick={clickable(f)}>
          {isImage(f) && (
            <div className="h-24 rounded overflow-hidden bg-muted/30 flex items-center justify-center">
              <FileThumb file={f} className="h-full w-full object-cover" />
            </div>
          )}
          {cardFields.includes('name') && <div className="text-xs font-medium truncate">{f.fileName}</div>}
          {cardFields.includes('size') && <div className="text-[10px] text-muted-foreground">Размер: {fmtSize(f.fileSize)}</div>}
          {cardFields.includes('uploaded_by') && f.uploadedBy && <div className="text-[10px] text-muted-foreground">Загрузил: {f.uploadedBy}</div>}
          {cardFields.includes('uploaded_at') && f.uploadedAt && <div className="text-[10px] text-muted-foreground">Загрузка: {fmtDate(f.uploadedAt)}</div>}
          {actions && <div className="flex items-center justify-end pt-1 border-t">{rowActions(f)}</div>}
        </div>
      ))}
    </div>
  );
}
